import React from 'react';
import { Award, BookOpen, Heart, Star, MapPin, Calendar } from 'lucide-react';

const About = () => {
  return (
    <div className="pt-20" data-testid="about-page">
      {/* Hero Section */}
      <section className="relative py-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-brand-moody/30 to-brand-charcoal" />
        
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-gold-500 tracking-[0.3em] uppercase text-sm mb-4">
            About Your Officiant
          </p>
          <h1 className="text-5xl md:text-6xl font-script text-gold-500 mb-4">
            Keeper Of The Fiyah
          </h1>
          <p className="text-warm-100/60 tracking-[0.2em] uppercase text-sm mb-8">
            Drip N Fiyah Ministry
          </p>
          <div className="divider-gold max-w-xs mx-auto" />
        </div>
      </section>

      {/* Main Content */}
      <section className="section-container">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
          {/* Image */}
          <div className="relative">
            <div className="aspect-[3/4] rounded-sm overflow-hidden border-gold-glow">
              <img
                src="https://images.unsplash.com/photo-1469371670807-013ccf25f16a?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80"
                alt="Wedding officiant"
                className="w-full h-full object-cover"
              />
            </div>
          </div>

          {/* Story */}
          <div>
            <h2 className="text-3xl font-serif text-warm-100 mb-6">
              A Calling to Celebrate Love
            </h2>
            
            <div className="space-y-6 text-warm-100/80 leading-relaxed">
              <p>
                My journey to ministry began with a simple truth: love, in all its forms, 
                deserves to be celebrated with reverence, joy, and authenticity.
              </p>
              <p>
                As an ordained minister, I have been blessed with the sacred responsibility 
                of officiating ceremonies that mark life's most precious moments. Each wedding, 
                vow renewal, or commitment ceremony I perform is approached with deep respect 
                for the unique bond shared between the individuals involved.
              </p>
              <p>
                I believe that every love story is worthy of a ceremony that reflects its 
                singular beauty. Whether you envision a traditional religious ceremony, 
                a spiritual but non-denominational celebration, or something entirely 
                personal to your journey together, I am here to help bring your vision to life.
              </p>
              
              <div className="scripture-quote my-8">
                <p className="italic">
                  "...He considered me faithful by assigning me to a ministry."
                </p>
                <p className="text-gold-500/80 mt-2 not-italic">— 1 Timothy 1:12</p>
              </div>

              <p>
                This scripture guides my work. Being entrusted with the honor of officiating 
                your ceremony is something I never take lightly. It is a privilege to stand 
                with you at the threshold of your new life together.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Credentials Section */}
      <section className="py-24 bg-brand-moody/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-gold-500 tracking-[0.3em] uppercase text-sm mb-4">
              Credentials & Qualifications
            </p>
            <h2 className="text-3xl md:text-4xl font-serif text-warm-100 mb-6">
              Licensed & Ordained
            </h2>
            <div className="divider-gold max-w-xs mx-auto" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: BookOpen,
                title: 'Ordained Minister',
                description: 'Legally ordained and authorized to perform wedding ceremonies in all 50 states.',
              },
              {
                icon: Award,
                title: 'Licensed Officiant',
                description: 'Fully licensed and registered with state and local authorities.',
              },
              {
                icon: Heart,
                title: 'All Faiths Welcome',
                description: 'Experienced in religious, spiritual, and secular ceremonies for all backgrounds.',
              },
              {
                icon: Star,
                title: 'Personalized Service',
                description: 'Every ceremony is custom-crafted to reflect your unique love story.',
              },
              {
                icon: MapPin,
                title: 'Travel Available',
                description: 'Willing to travel to your chosen venue, wherever your heart leads.',
              },
              {
                icon: Calendar,
                title: 'Flexible Scheduling',
                description: 'Accommodating various dates, times, and last-minute arrangements.',
              },
            ].map((item, index) => {
              const IconComponent = item.icon;
              return (
                <div
                  key={index}
                  className="card-elegant p-6 text-center"
                  data-testid={`credential-${index}`}
                >
                  <div className="w-14 h-14 rounded-full bg-gold-500/10 flex items-center justify-center mx-auto mb-4">
                    <IconComponent className="w-7 h-7 text-gold-500" />
                  </div>
                  <h3 className="text-xl font-serif text-warm-100 mb-2">
                    {item.title}
                  </h3>
                  <p className="text-warm-100/60 text-sm">
                    {item.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Philosophy Section */}
      <section className="section-container">
        <div className="max-w-3xl mx-auto text-center">
          <p className="text-gold-500 tracking-[0.3em] uppercase text-sm mb-4">
            My Philosophy
          </p>
          <h2 className="text-3xl md:text-4xl font-serif text-warm-100 mb-8">
            Honoring Sacred Unions
          </h2>
          
          <div className="space-y-6 text-warm-100/80 leading-relaxed text-lg">
            <p>
              A wedding ceremony is more than words spoken—it's a sacred moment where 
              two souls publicly declare their commitment to journey through life together.
            </p>
            <p>
              My role is to create a space where that commitment can be expressed authentically, 
              witnessed lovingly, and remembered forever. I bring presence, warmth, and 
              attention to every detail, ensuring your ceremony flows seamlessly and 
              touches the hearts of all present.
            </p>
            <p className="text-gold-500 font-serif text-2xl mt-8">
              "Every love story deserves a ceremony as unique as the souls it unites."
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
